import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';

/**
 * Native Support Chat API for the in-app support screen.
 *
 * Mirrors the auth/error conventions of userApi.js:
 *   - Bearer token from secure storage on every call.
 *   - JSON responses parsed defensively (204 / empty body tolerated).
 *
 * Backend contract (HappyPOS-backend support-chat controller). Conversations are
 * AI-first: the assistant answers until the user explicitly requests a human, at
 * which point the conversation is escalated and (when no agent is available) an
 * issue-creation fallback is offered.
 *
 * Endpoints (base `${apiURL}/api/support-chat`):
 *   - POST   conversations                       create a conversation
 *   - GET    conversations/mine?active=true      list my conversations
 *   - GET    conversations/{id}                  fetch one conversation
 *   - POST   conversations/{id}/messages         post a message
 *   - POST   conversations/{id}/request-agent    escalate to a human
 *   - POST   conversations/{id}/close            close + lock
 *   - POST   conversations/{id}/issue            create a support issue (ticket)
 *   - POST   conversations/{id}/read             mark as read
 *   - POST   conversations/{id}/attachments      multipart upload (field `file`)
 */

const SUPPORT_URL = `${ApiConfig.apiURL}/api/support-chat`;

/**
 * Build a meaningful Error from a non-ok fetch Response, preferring the API's
 * own `message`/`error` body when present.
 * @param {Response} response
 * @returns {Promise<Error>}
 */
const buildErrorFromResponse = async (response) => {
    try {
        const body = await response.json();
        const message = body?.message || body?.error || response.statusText;
        const error = new Error(message || `Request failed with status ${response.status}`);
        error.httpStatus = response.status;
        return error;
    } catch (_error) {
        const error = new Error(response.statusText || `Request failed with status ${response.status}`);
        error.httpStatus = response.status;
        return error;
    }
};

/**
 * Parse a fetch Response defensively (tolerates 204 / empty bodies).
 * @param {Response} response
 * @returns {Promise<any>}
 */
const parseApiResponse = async (response) => {
    if (!response.ok) {
        throw await buildErrorFromResponse(response);
    }
    if (response.status === 204) {
        return true;
    }
    const text = await response.text();
    if (!text) {
        return true;
    }
    try {
        return JSON.parse(text);
    } catch (_error) {
        return text;
    }
};

/**
 * Resolve the JSON auth headers, throwing a tagged error when unauthenticated.
 * @returns {Promise<{'Content-Type': string, Authorization: string}>}
 */
const authHeaders = async () => {
    const accessToken = await getAccessToken();
    if (!accessToken) {
        const authError = new Error('Authentication required');
        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
    };
};

/**
 * Start a new support conversation. The backend seeds the AI greeting/first
 * answer, so the returned conversation already contains messages.
 *
 * @param {{message?: string, subject?: string, mode?: string, locale?: string}} [payload]
 * @returns {Promise<object>} the created conversation
 */
export async function createSupportConversation({message, subject, mode, locale} = {}) {
    const body = {channel: 'mobile'};
    if (message) body.message = message;
    if (subject) body.subject = subject;
    if (mode) body.mode = mode;
    if (locale) body.locale = locale;

    const response = await fetch(`${SUPPORT_URL}/conversations`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(body),
    });
    return parseApiResponse(response);
}

/**
 * List the current user's support conversations.
 *
 * @param {{active?: boolean}} [options] when `active` is true only open
 *   (non-closed) conversations are returned.
 * @returns {Promise<object[]>} the conversation list (always an array)
 */
export async function getMySupportConversations({active = false} = {}) {
    const query = active ? '?active=true' : '';
    const response = await fetch(`${SUPPORT_URL}/conversations/mine${query}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    const data = await parseApiResponse(response);
    const items = data?.items;
    return Array.isArray(items) ? items : (Array.isArray(data) ? data : []);
}

/**
 * Fetch a single conversation by id (includes its full message history).
 *
 * @param {string} conversationId
 * @returns {Promise<object>}
 */
export async function getSupportConversation(conversationId) {
    const response = await fetch(`${SUPPORT_URL}/conversations/${conversationId}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parseApiResponse(response);
}

/**
 * Post a user message into a conversation. The backend appends the message and
 * (in AI mode) the assistant's reply, returning the updated conversation.
 *
 * @param {string} conversationId
 * @param {{content: string, mode?: string, locale?: string, attachments?: object[]}} payload
 * @returns {Promise<object>} the updated conversation
 */
export async function sendSupportMessage(conversationId, {content, mode, locale, attachments} = {}) {
    const body = {content: content || ''};
    if (mode) body.mode = mode;
    if (locale) body.locale = locale;
    if (Array.isArray(attachments) && attachments.length) body.attachments = attachments;

    const response = await fetch(`${SUPPORT_URL}/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(body),
    });
    return parseApiResponse(response);
}

/**
 * Escalate a conversation to a human agent.
 *
 * @param {string} conversationId
 * @param {{reason?: string}} [payload]
 * @returns {Promise<object>} the updated conversation
 */
export async function requestSupportAgent(conversationId, {reason} = {}) {
    const response = await fetch(`${SUPPORT_URL}/conversations/${conversationId}/request-agent`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(reason ? {reason} : {}),
    });
    return parseApiResponse(response);
}

/**
 * Close a conversation. Passing `lock: true` permanently locks it server-side.
 *
 * @param {string} conversationId
 * @param {{lock?: boolean}} [options]
 * @returns {Promise<object>} the updated conversation
 */
export async function closeSupportConversation(conversationId, {lock = true} = {}) {
    const response = await fetch(`${SUPPORT_URL}/conversations/${conversationId}/close`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({lock}),
    });
    return parseApiResponse(response);
}

/**
 * Create a support issue (ticket) from a conversation. The backend auto-fills
 * the transcript; the returned conversation carries a `.ticket`.
 *
 * @param {string} conversationId
 * @param {{title?: string, description?: string, priority?: string}} [payload]
 * @returns {Promise<object>} the updated conversation (with `.ticket`)
 */
export async function createSupportIssue(conversationId, {title, description, priority} = {}) {
    const body = {};
    if (title) body.title = title;
    if (description) body.description = description;
    if (priority) body.priority = priority;

    const response = await fetch(`${SUPPORT_URL}/conversations/${conversationId}/issue`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(body),
    });
    return parseApiResponse(response);
}

/**
 * Mark a conversation as read for the current user. Best-effort; failures are
 * non-fatal and should be swallowed by the caller.
 *
 * @param {string} conversationId
 * @returns {Promise<any>}
 */
export async function markSupportConversationRead(conversationId) {
    const response = await fetch(`${SUPPORT_URL}/conversations/${conversationId}/read`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({}),
    });
    return parseApiResponse(response);
}

/**
 * Upload an attachment (multipart/form-data, field `file`). The returned
 * `attachment` descriptor must be included in the next message's `attachments`.
 *
 * @param {string} conversationId
 * @param {{uri: string, name?: string, type?: string, mimeType?: string}} asset
 *   image/document asset (expo-image-picker / expo-document-picker shape)
 * @returns {Promise<{attachment: {filename: string, url: string, mimeType: string, size: number}}>}
 */
export async function uploadSupportAttachment(conversationId, asset) {
    const accessToken = await getAccessToken();
    if (!accessToken) {
        const authError = new Error('Authentication required');
        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }

    const uri = asset?.uri;
    if (!uri) {
        throw new Error('No file selected');
    }

    const guessedName = asset?.name || asset?.fileName || uri.split('/').pop() || `attachment-${Date.now()}`;
    const guessedType = asset?.mimeType
        || asset?.type
        || (/\.png$/i.test(guessedName) ? 'image/png'
            : /\.(jpe?g)$/i.test(guessedName) ? 'image/jpeg'
                : /\.pdf$/i.test(guessedName) ? 'application/pdf'
                    : 'application/octet-stream');

    const formData = new FormData();
    // RN's fetch builds the multipart boundary itself — do NOT set Content-Type.
    formData.append('file', {uri, name: guessedName, type: guessedType});

    const response = await fetch(`${SUPPORT_URL}/conversations/${conversationId}/attachments`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
        },
        body: formData,
    });
    return parseApiResponse(response);
}
